import { useCallback, useEffect, useRef, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Drawer from '../components/Drawer';
import Modal from '../components/Modal';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { date } from '../lib/format';
import { resizeImage } from '../lib/image';

// One photo tile: lazily fetches its image, caption editable below, click to enlarge.
function PhotoTile({ reportId, photo, canEdit, onDelete, onOpen, onCaption }) {
  const [src, setSrc] = useState(null);
  const [cap, setCap] = useState(photo.caption || '');
  useEffect(() => {
    let live = true;
    api(`/service-reports/${reportId}/photos/${photo.id}`).then((d) => { if (live) setSrc(d.image_data); }).catch(() => {});
    return () => { live = false; };
  }, [reportId, photo.id]);
  return (
    <div>
      <div className="photo-tile">
        {src ? <img src={src} alt={photo.caption || ''} onClick={() => onOpen(src)} />
             : <div className="photo-tile__ph">…</div>}
        {canEdit && <button className="photo-tile__rm" title="Delete" onClick={() => onDelete(photo.id)}>×</button>}
      </div>
      {canEdit
        ? <input value={cap} placeholder="Caption" style={{ marginTop: 4, fontSize: 12 }}
            onChange={(e) => setCap(e.target.value)}
            onBlur={() => { if (cap !== (photo.caption || '')) onCaption(photo.id, cap); }} />
        : (photo.caption ? <div className="hint" style={{ marginTop: 4 }}>{photo.caption}</div> : null)}
    </div>
  );
}

const EMPTY = {
  depart_date: '', return_date: '', service_dates: '', invoice_no: '', system_type: '',
  customer_name: '', contact_name: '', contact_email: '', contact_phone: '',
  service_address: '', city: '', state: '', postal_code: '', country: '',
  tech_lead: '', tech_2: '', tech_3: '',
  tasks: '', observations: '', issues: '', corrective_actions: '',
  jobboss_entered_by: '', jobboss_entered_date: '',
};

const toForm = (r) => Object.fromEntries(Object.keys(EMPTY).map((k) => {
  const v = r[k] == null ? '' : String(r[k]);
  return [k, k.endsWith('_date') ? v.slice(0, 10) : v];
}));

export default function ServiceReports() {
  const { activeId, activeBusiness } = useBusiness();
  const { can } = useAuth();
  const editable = can('service_reports.manage');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [newReq, setNewReq] = useState(null);   // { customer_id } while creating
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [send, setSend] = useState(null);       // { to, cc, note } when the send panel is open
  const [photoBusy, setPhotoBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);

  const loadList = useCallback(() => {
    setLoading(true);
    api(`/service-reports?business_id=${activeId}`).then((d) => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [activeId]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    api(`/customers?business_id=${activeId}`).then((d) => setCustomers((Array.isArray(d) ? d : []).filter((c) => c.is_active !== false))).catch(() => setCustomers([]));
  }, [activeId]);

  function openNew() {
    setCurrent(null); setForm(null); setSend(null); setError(null);
    setNewReq({ customer_id: '' });
    setOpen(true);
  }
  async function openRow(row) {
    setNewReq(null); setSend(null); setError(null); setOpen(true);
    try {
      const r = await api(`/service-reports/${row.id}`);
      setCurrent(r); setForm(toForm(r));
    } catch { setOpen(false); }
  }

  async function createDraft() {
    setBusy(true); setError(null);
    try {
      const r = await api('/service-reports', { method: 'POST', body: { business_id: activeId, customer_id: newReq.customer_id || undefined } });
      setCurrent(r); setForm(toForm(r)); setNewReq(null); loadList();
    } catch (e) { setError(e?.detail || 'Could not create.'); }
    finally { setBusy(false); }
  }

  async function save() {
    setSaving(true); setError(null);
    try {
      const r = await api(`/service-reports/${current.id}`, { method: 'PUT', body: form });
      setCurrent(r); setForm(toForm(r)); loadList();
      return r;
    } catch (e) { setError(e?.detail || 'Could not save.'); return null; }
    finally { setSaving(false); }
  }

  async function action(path, label) {
    setBusy(true); setError(null);
    try {
      const r = await api(`/service-reports/${current.id}/${path}`, { method: 'POST' });
      setCurrent(r); setForm(toForm(r)); loadList();
    } catch (e) { setError(e?.detail || `Could not ${label}.`); }
    finally { setBusy(false); }
  }

  async function preview() {
    const r = await save();
    if (r) window.open(`/api/service-reports/${current.id}/pdf`, '_blank', 'noopener');
  }

  async function openSend() {
    const r = await save();
    if (r) setSend({ to: r.contact_email || '', cc: '', note: '' });
  }
  async function doSend() {
    setBusy(true); setError(null);
    try {
      const resp = await api(`/service-reports/${current.id}/send`, { method: 'POST', body: send });
      setCurrent(resp.report); setForm(toForm(resp.report)); setSend(null); loadList();
    } catch (e) { setError(e?.detail || 'Could not send.'); }
    finally { setBusy(false); }
  }

  async function removeReport() {
    if (!window.confirm('Delete this service report? This cannot be undone.')) return;
    setBusy(true); setError(null);
    try {
      await api(`/service-reports/${current.id}`, { method: 'DELETE' });
      setOpen(false); loadList();
    } catch (e) { setError(e?.detail || 'Could not delete.'); }
    finally { setBusy(false); }
  }

  // ---- photos ----
  async function refreshPhotos() {
    try { const r = await api(`/service-reports/${current.id}`); setCurrent(r); } catch { /* ignore */ }
  }
  async function onPickPhotos(kind, files) {
    if (!current || !files?.length) return;
    setPhotoBusy(true); setError(null);
    try {
      for (const file of Array.from(files)) {
        const image = await resizeImage(file, 1400, 0.75); // keep the emailed PDF small
        await api(`/service-reports/${current.id}/photos`, { method: 'POST', body: { kind, image } });
      }
      await refreshPhotos();
    } catch (e) { setError(e?.detail || e?.message || 'Photo upload failed.'); }
    finally { setPhotoBusy(false); }
  }
  async function deletePhoto(photoId) {
    try { await api(`/service-reports/${current.id}/photos/${photoId}`, { method: 'DELETE' }); await refreshPhotos(); } catch { /* ignore */ }
  }
  async function setCaption(photoId, caption) {
    try { await api(`/service-reports/${current.id}/photos/${photoId}`, { method: 'PUT', body: { caption } }); await refreshPhotos(); } catch { /* ignore */ }
  }
  const photosByKind = (kind) => (current?.photos || []).filter((p) => p.kind === kind);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const input = (k, label, type = 'text') => (
    <div className="field"><label>{label}</label>
      <input type={type} value={form[k]} onChange={set(k)} disabled={!editable} /></div>
  );
  const area = (k, label, rows = 5) => (
    <div className="field field--full"><label>{label}</label>
      <textarea rows={rows} value={form[k]} placeholder="One bullet per line" onChange={set(k)} disabled={!editable} /></div>
  );

  const columns = [
    { key: 'customer_name', header: 'Customer', render: (r) => r.customer_name || '—' },
    { key: 'service_dates', header: 'Service dates', render: (r) => r.service_dates || [r.depart_date, r.return_date].filter(Boolean).map(date).join(' to ') || '—' },
    { key: 'invoice_no', header: 'Invoice #', render: (r) => r.invoice_no || '—' },
    { key: 'tech_lead', header: 'Lead tech', render: (r) => r.tech_lead || '—' },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'created_at', header: 'Created', render: (r) => date(r.created_at) },
  ];

  const footer = current ? (
    send ? (
      <>
        <button className="btn" onClick={() => setSend(null)} disabled={busy}>Cancel</button>
        <button className="btn btn--primary" onClick={doSend} disabled={busy || !send.to.trim()}>{busy ? 'Sending…' : 'Send PDF'}</button>
      </>
    ) : (
      <>
        <button className="btn" onClick={() => !saving && setOpen(false)}>Close</button>
        <button className="btn" onClick={preview} disabled={saving}>Preview PDF</button>
        {editable && <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>}
        {editable && <button className="btn btn--primary" onClick={openSend} disabled={saving || busy}>{current.status === 'sent' ? 'Re-send PDF' : 'Email PDF'}</button>}
      </>
    )
  ) : (
    <>
      <button className="btn" onClick={() => setOpen(false)}>Cancel</button>
      <button className="btn btn--primary" onClick={createDraft} disabled={busy}>{busy ? 'Creating…' : 'Create report'}</button>
    </>
  );

  return (
    <>
      <PageHeader title="Service Reports" subtitle={activeBusiness?.name}
        actions={editable && <button className="btn btn--primary" onClick={openNew}>New report</button>} />

      <section className="panel">
        <DataTable columns={columns} rows={rows} loading={loading} onRowClick={openRow} empty="No service reports yet." />
      </section>

      <Drawer open={open} title={current ? (current.customer_name || 'Service report') : 'New service report'} width={720} onClose={() => !saving && !busy && setOpen(false)} footer={footer}>
        {newReq && !current && (
          <>
            <div className="field"><label>Customer (optional)</label>
              <select value={newReq.customer_id} onChange={(e) => setNewReq({ customer_id: e.target.value })}>
                <option value="">None — enter details manually</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.company || c.name}</option>)}
              </select>
            </div>
            <p className="hint">Picking a customer prefills the company, contact, and address fields. Everything stays editable on the report.</p>
            {error && <div className="form-error">{error}</div>}
          </>
        )}

        {current && form && !send && (
          <>
            <div className="appr-status">
              <StatusBadge status={current.status} />
              {current.status === 'sent' && current.sent_at && <span className="hint">Sent {date(current.sent_at)} to {current.sent_to}</span>}
              {current.status === 'completed' && current.completed_at && <span className="hint">Completed {date(current.completed_at)}</span>}
            </div>

            <h3 className="form-section">Trip</h3>
            <div className="form-grid">
              {input('depart_date', 'Departure date', 'date')}
              {input('return_date', 'Return date', 'date')}
              {input('service_dates', 'On-site service dates (as printed)')}
              {input('invoice_no', 'On-site invoice #')}
              {input('system_type', 'System type')}
            </div>

            <h3 className="form-section">Customer</h3>
            <div className="form-grid">
              <div className="field field--full"><label>Customer name (company)</label>
                <input value={form.customer_name} onChange={set('customer_name')} disabled={!editable} /></div>
              {input('contact_name', 'Contact name')}
              {input('contact_email', 'Email')}
              {input('contact_phone', 'Phone')}
              {input('service_address', 'Service address')}
              {input('city', 'City')}
              {input('state', 'State / province')}
              {input('postal_code', 'Zip / postal code')}
              {input('country', 'Country')}
            </div>

            <h3 className="form-section">Technicians</h3>
            <div className="form-grid">
              {input('tech_lead', 'Service technician lead')}
              {input('tech_2', 'Service technician #2')}
              {input('tech_3', 'Service technician #3')}
            </div>

            <h3 className="form-section">Report</h3>
            <div className="form-grid">
              {area('tasks', 'Service tasks')}
              {area('observations', 'Service observation and information')}
              {area('issues', 'Service issues')}
              {area('corrective_actions', 'Corrective actions')}
            </div>

            <h3 className="form-section">Repair photos</h3>
            {['before', 'after'].map((kind) => (
              <div className="photo-group" key={kind}>
                <div className="photo-group__head">
                  <span className="photo-group__label">{kind === 'before' ? 'Before' : 'After'}</span>
                  {editable && (
                    <>
                      <button className="btn btn--sm" disabled={photoBusy} onClick={() => (kind === 'before' ? beforeRef : afterRef).current?.click()}>{photoBusy ? 'Uploading…' : '+ Add'}</button>
                      <input ref={kind === 'before' ? beforeRef : afterRef} type="file" accept="image/*" capture="environment" multiple
                        style={{ display: 'none' }} onChange={(e) => { onPickPhotos(kind, e.target.files); e.target.value = ''; }} />
                    </>
                  )}
                </div>
                {photosByKind(kind).length === 0 ? <p className="hint">No {kind} photos.</p> : (
                  <div className="photo-grid">
                    {photosByKind(kind).map((p) => (
                      <PhotoTile key={p.id} reportId={current.id} photo={p} canEdit={editable}
                        onDelete={deletePhoto} onOpen={setLightbox} onCaption={setCaption} />
                    ))}
                  </div>
                )}
              </div>
            ))}

            <h3 className="form-section">Entered into JobBOSS</h3>
            <div className="form-grid">
              {input('jobboss_entered_by', 'Entered by')}
              {input('jobboss_entered_date', 'Date entered', 'date')}
            </div>

            {error && <div className="form-error" style={{ marginTop: 10 }}>{error}</div>}
            {editable && (
              <div className="quote-actions" style={{ marginTop: 14 }}>
                {current.status === 'draft' && <button className="btn btn--sm" onClick={() => action('complete', 'complete')} disabled={busy}>Mark completed</button>}
                {current.status === 'completed' && <button className="btn btn--sm" onClick={() => action('reopen', 'reopen')} disabled={busy}>Reopen</button>}
                <button className="btn btn--sm btn--danger" onClick={removeReport} disabled={busy}>Delete</button>
              </div>
            )}
          </>
        )}

        {current && send && (
          <>
            <p className="hint">The report is saved and will be attached as a PDF.</p>
            <div className="field"><label>To</label>
              <input value={send.to} onChange={(e) => setSend((s) => ({ ...s, to: e.target.value }))} placeholder="name@company.com" /></div>
            <div className="field"><label>Cc (optional, comma separated)</label>
              <input value={send.cc} onChange={(e) => setSend((s) => ({ ...s, cc: e.target.value }))} /></div>
            <div className="field"><label>Note (optional, included in the email)</label>
              <textarea rows={3} value={send.note} onChange={(e) => setSend((s) => ({ ...s, note: e.target.value }))} /></div>
            {error && <div className="form-error">{error}</div>}
          </>
        )}
      </Drawer>

      <Modal open={!!lightbox} title="Photo" onClose={() => setLightbox(null)}>
        {lightbox && <img src={lightbox} alt="" style={{ maxWidth: '100%', borderRadius: 8 }} />}
      </Modal>
    </>
  );
}
